// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the things Konvo read, shown as cards under a reply. Each card carries
// the assistant's snapshot (who, when, a line of it) so it reads on its own, and
// opens the real message in the preview window.

import styles from '@app/features/konvo_assistant/components/AssistantSourceCards.module.css';
import {KonvoCardHead} from '@app/features/konvo_assistant/components/card/KonvoCardHead';
import {isRailWindow} from '@app/features/konvo_rail/utils/RailWindow';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {ArrowsOutIcon} from '@app/features/ui/icons/Icons';
import type {AssistantSourceBlock, AssistantSourceItem} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import {useLingui} from '@lingui/react/macro';
import {IconCheckbox, IconMail, IconMessageCircle, IconMessages, IconStack2, IconUsers} from '@tabler/icons-react';

/* The preview window pulls in the mail, WhatsApp, task and channel stores, so it
 * only loads when someone actually opens a source. The Rail has no room for it and
 * never navigates itself: there the source opens in the main window. */
export function openPreview(item: AssistantSourceItem, target?: HTMLElement | null): void {
	if (isRailWindow()) {
		void import('@app/features/konvo_rail/RailAssistantOpen').then((module) => module.openSourceFromRail(item));
		return;
	}
	void import('@app/features/konvo_assistant/components/AssistantSourcePreview').then((module) =>
		module.openSourcePreview(item, target),
	);
}

export function SourceMark({kind, size = 18}: {kind: AssistantSourceItem['kind']; size?: number}) {
	switch (kind) {
		case 'mail':
			return <OutlookLogo size={size} />;
		case 'whatsapp':
			return <WhatsAppLogo size={size} />;
		case 'teams':
			return <TeamsLogo size={size} />;
		case 'clickup':
			return <ClickUpLogo size={size} />;
		case 'konvo':
			return <KonvoLogo size={size} />;
	}
}

export function sourceKindLabel(kind: AssistantSourceItem['kind']): string {
	switch (kind) {
		case 'mail':
			return 'Outlook';
		case 'whatsapp':
			return 'WhatsApp';
		case 'teams':
			return 'Teams';
		case 'clickup':
			return 'ClickUp';
		case 'konvo':
			return 'Konvo';
	}
}

/** Where the sources came from, for the card's header: one kind, or several. */
function SourcesHead({items}: {items: AssistantSourceBlock['items']}) {
	const {t} = useLingui();
	const kinds = new Set(items.map((item) => item.kind));
	const kind = kinds.size === 1 ? items[0]?.kind : undefined;
	const count = items.length;
	const end = count === 1 ? t`1 source` : t`${count} sources`;
	switch (kind) {
		case 'mail':
			return <KonvoCardHead icon={<IconMail stroke={1.75} />} label={t`From your mail`} end={end} />;
		case 'whatsapp':
			return <KonvoCardHead icon={<IconMessageCircle stroke={1.75} />} label={t`From WhatsApp`} end={end} />;
		case 'teams':
			return <KonvoCardHead icon={<IconUsers stroke={1.75} />} label={t`From Teams`} end={end} />;
		case 'clickup':
			return <KonvoCardHead icon={<IconCheckbox stroke={1.75} />} label={t`From ClickUp`} end={end} />;
		case 'konvo':
			return <KonvoCardHead icon={<IconMessages stroke={1.75} />} label={t`From your chats`} end={end} />;
		default:
			return <KonvoCardHead icon={<IconStack2 stroke={1.75} />} label={t`Sources`} end={end} />;
	}
}

export function AssistantSourceCards({block}: {block: AssistantSourceBlock}) {
	const {t} = useLingui();
	// KONVO: one card (design/assistant-chat-unify): the shared header, the block's title, then the
	// sources as rows with their open icon in one column.
	return (
		<section className={styles.block} data-flx="konvo-assistant.source-cards">
			<SourcesHead items={block.items} />
			{block.title && <h3 className={styles.blockTitle}>{block.title}</h3>}
			<div className={styles.cards}>
				{block.items.map((item, index) => (
					<button
						key={`${item.kind}:${item.message_id ?? item.task_id ?? item.url ?? index}`}
						type="button"
						className={styles.card}
						onClick={(event) => openPreview(item, event.currentTarget)}
						title={t`Open the preview`}
						data-flx="konvo-assistant.source-cards.card"
					>
						<span className={styles.mark} aria-hidden="true">
							<SourceMark kind={item.kind} />
						</span>
						<span className={styles.text}>
							<span className={styles.title}>{item.title}</span>
							<span className={styles.meta}>
								{[sourceKindLabel(item.kind), item.subtitle, item.meta].filter(Boolean).join(' · ')}
							</span>
							{item.snippet && <span className={styles.snippet}>{item.snippet}</span>}
						</span>
						<span className={styles.open} aria-hidden="true">
							<ArrowsOutIcon size={16} />
						</span>
					</button>
				))}
			</div>
		</section>
	);
}
